<template>
  <div v-if="initialLoadComplete">
    <v-navigation-drawer
      class="och-topMargin--packInfoOffset"
      v-if="releaseNoteId"
      v-model="drawer"
      absolute
      right
      mobile-breakpoint="1"
      width="300px"
      style="z-index: 1000"
    >
      <template v-slot:prepend>
        <v-list-item two-line class="och-navigation-drawer">
          <v-btn fab color="primary" @click="updateDrawer(false, drawerMode)" small>
            <v-icon color="white">fas fa-arrow-right</v-icon>
          </v-btn>
        </v-list-item>
        <v-divider></v-divider>
      </template>

      <div v-if="drawerMode === `attachments`" class="px-3">
        <file-uploader :ownerId="releaseNoteId" ownerType="releaseNote" @insertFile="insertFile"></file-uploader>
      </div>
      <div v-if="drawerMode === `fields`">
        <v-list v-if="releaseNoteRecord.templateType">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-wrap">
                Toggle optional fields below. Required fields are always visible and cannot be hidden.
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-for="field in sortedFields" :key="field.key">
            <v-list-item-action>
              <v-checkbox v-model="templateMask.fields[field.key]" :false-value="false" :true-value="true"></v-checkbox>
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title>{{ field.label }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </div>
      <div>
        <v-btn @click="updateDrawer(false)" class="ml-2" text>
          <v-icon small class="mr-2">fas fa-times</v-icon>Close
        </v-btn>
      </div>
    </v-navigation-drawer>
    <v-container class="och-releaseNote__form" fluid>
      <v-row justify="center" no-gutters>
        <v-col cols="10">
          <v-form v-model="formValid">
            <div
              class="d-flex align-center flex-wrap mb-4 caption"
              data-cy="releaseNoteForm__text--sourceTemplate"
              v-if="releaseNoteRecord.sourceTemplateId"
            >
              <v-icon x-small class="mr-2">fas fa-clone</v-icon>
              <span class="mr-1">Copied from template:</span>
              <router-link
                :to="{
                  name: 'releaseNoteView',
                  params: {
                    packId: releaseNoteRecord.sourceTemplatePackId,
                    releaseNoteId: releaseNoteRecord.sourceTemplateId,
                  },
                }"
                data-cy="releaseNoteForm__link--sourceTemplate"
                target="_blank"
              >
                {{ sourceTemplateDisplay }}
              </router-link>
              <span class="ml-2 font-weight-medium" data-cy="releaseNoteForm__text--assignQaAnalyst" v-if="needsQaAnalyst">
                Assign a QA Analyst and review the copied content.
              </span>
            </div>
            <v-row dense>
              <v-col cols="12">
                <v-btn
                  v-if="releaseNoteId"
                  value="attachments"
                  @click="updateDrawer(!drawer, `attachments`)"
                  :class="`${drawerMode === 'attachments' ? 'primary' : ''} mx-0 elevation-1 mr-1`"
                  small
                >
                  <v-icon small class="mr-2">fas fa-paperclip</v-icon>
                  <span>Attachments</span>
                </v-btn>
                <v-btn
                  value="fields"
                  @click="updateDrawer(!drawer, `fields`)"
                  :class="`${drawerMode === 'fields' ? 'primary' : ''} mx-0 elevation-1`"
                  small
                >
                  <v-icon small class="mr-2">far fa-list-alt</v-icon>
                  <span>Field Settings</span>
                </v-btn>
              </v-col>
            </v-row>
            <v-row dense align="end">
              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="templateTypes"
                  :rules="templateTypeRules"
                  @change="handleTemplateTypeChange()"
                  data-cy="releaseNoteForm__input--templateType"
                  dense
                  filled
                  item-text="name"
                  item-value="value"
                  label="Template Type *"
                  v-model="releaseNoteRecord.templateType"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="dropdowns.documentationStatus"
                  :rules="documentationStatusRules"
                  data-cy="releaseNoteForm__input--documentationStatus"
                  dense
                  filled
                  label="Documentation Status *"
                  v-model="releaseNoteRecord.documentationStatus"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="dropdowns.careSetting"
                  :rules="careSettingRules"
                  data-cy="releaseNoteForm__input--careSettingDropdown"
                  data-test="careSetting"
                  dense
                  filled
                  required
                  label="Care Setting *"
                  v-model="releaseNoteRecord.careSetting"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col
                class="mt-2"
                cols="12"
                md="3"
                v-if="templateMask.fields.applicationDropdownValueId || releaseNoteRecord.applicationDropdownValueId"
              >
                <admin-dropdown
                  :dropdownList="adminDropdownList"
                  :required="true"
                  :value="releaseNoteRecord.applicationDropdownValueId"
                  @selectValue="selectApplication"
                  dropdownAttributeName="applicationDropdownValueId"
                ></admin-dropdown>
              </v-col>
              <v-col
                class="mt-2"
                cols="12"
                md="3"
                v-if="
                  templateMask.fields.primaryActivityDropdownValueId || releaseNoteRecord.primaryActivityDropdownValueId
                "
              >
                <admin-dropdown
                  :dropdownList="adminDropdownList"
                  :grouperValue="releaseNoteRecord.applicationDropdownValueId"
                  :required="true"
                  :value="releaseNoteRecord.primaryActivityDropdownValueId"
                  @selectValue="selectPrimaryActivity"
                  dropdownAttributeName="primaryActivityDropdownValueId"
                ></admin-dropdown>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-checkbox
                  :false-value="0"
                  :true-value="1"
                  data-cy="releaseNoteForm__input--isGovernedTemplate"
                  dense
                  hide-details
                  label="Governed Template"
                  v-model="releaseNoteRecord.isGovernedTemplate"
                ></v-checkbox>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" class="mb-7">
                <v-autocomplete
                  :items="packList"
                  color="primary darken-2"
                  data-cy="releaseNoteForm__packDropdown"
                  dense
                  filled
                  hide-details
                  item-text="title"
                  item-value="id"
                  label="Pack"
                  v-model="releaseNoteRecord.packId"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-textarea
              auto-grow
              dense
              data-cy="releaseNoteForm__input--reopenedReason"
              label="Reopened Reason"
              outlined
              rows="1"
              v-if="releaseNoteRecord.documentationStatus === 'Reopened'"
              v-model="releaseNoteRecord.reopenedReason"
            ></v-textarea>
            <v-text-field
              :rules="titleRules"
              data-cy="releaseNoteForm__input--title"
              dense
              label="Title *"
              outlined
              required
              v-model="releaseNoteRecord.title"
            ></v-text-field>
            <v-expansion-panels
              focusable
              class="subtitle-1 mb-2"
              v-show="templateMask.fields.summary || releaseNoteRecord.summary"
            >
              <v-expansion-panel data-cy="releaseNoteForm__input--summary">
                <v-expansion-panel-header>Summary</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <ckeditor
                      :config="ckeditorConfig"
                      @focus="setCurrentEditor($event)"
                      id="summary"
                      v-model="releaseNoteRecord.summary"
                    ></ckeditor>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-expansion-panels
              focusable
              class="subtitle-1 mb-2"
              v-show="templateMask.fields.issue || releaseNoteRecord.issue"
            >
              <v-expansion-panel data-cy="releaseNoteForm__input--issue">
                <v-expansion-panel-header>Issue</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <ckeditor
                      :config="ckeditorConfig"
                      @focus="setCurrentEditor($event)"
                      id="issue"
                      v-model="releaseNoteRecord.issue"
                    ></ckeditor>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-expansion-panels
              focusable
              class="subtitle-1 mb-2"
              v-show="templateMask.fields.preGoLiveSetup || releaseNoteRecord.preGoLiveSetup"
            >
              <v-expansion-panel data-cy="releaseNoteForm__input--preGoLiveSetup">
                <v-expansion-panel-header>Go-Live Setup</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <ckeditor
                      :config="ckeditorConfig"
                      @focus="setCurrentEditor($event)"
                      id="preGoLiveSetup"
                      v-model="releaseNoteRecord.preGoLiveSetup"
                    ></ckeditor>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-expansion-panels
              focusable
              class="subtitle-1 mb-2"
              v-show="templateMask.fields.background || releaseNoteRecord.background"
            >
              <v-expansion-panel data-cy="releaseNoteForm__input--background">
                <v-expansion-panel-header>Background</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <ckeditor
                      :config="ckeditorConfig"
                      @focus="setCurrentEditor($event)"
                      id="background"
                      v-model="releaseNoteRecord.background"
                    ></ckeditor>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-expansion-panels
              focusable
              class="subtitle-1 mb-2"
              v-show="templateMask.fields.currentBehavior || releaseNoteRecord.currentBehavior"
            >
              <v-expansion-panel data-cy="releaseNoteForm__input--currentBehavior">
                <v-expansion-panel-header>Current Behavior</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <ckeditor
                      :config="ckeditorConfig"
                      @focus="setCurrentEditor($event)"
                      id="currentBehavior"
                      v-model="releaseNoteRecord.currentBehavior"
                    ></ckeditor>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-expansion-panels
              focusable
              class="subtitle-1 mb-2"
              v-show="templateMask.fields.newBehavior || releaseNoteRecord.newBehavior"
            >
              <v-expansion-panel data-cy="releaseNoteForm__input--newBehavior">
                <v-expansion-panel-header>New Behavior</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <ckeditor
                      :config="ckeditorConfig"
                      @focus="setCurrentEditor($event)"
                      id="newBehavior"
                      v-model="releaseNoteRecord.newBehavior"
                    ></ckeditor>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-expansion-panels
              focusable
              class="subtitle-1 mb-2"
              v-show="templateMask.fields.optionalEnhancement || releaseNoteRecord.optionalEnhancement"
            >
              <v-expansion-panel data-cy="releaseNoteForm__input--optionalEnhancement">
                <v-expansion-panel-header>Optional Enhancement</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <ckeditor
                      :config="ckeditorConfig"
                      @focus="setCurrentEditor($event)"
                      id="optionalEnhancement"
                      v-model="releaseNoteRecord.optionalEnhancement"
                    ></ckeditor>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-expansion-panels
              focusable
              class="subtitle-1 mb-2"
              v-show="templateMask.fields.note || releaseNoteRecord.note"
            >
              <v-expansion-panel data-cy="releaseNoteForm__input--note">
                <v-expansion-panel-header>Note</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <ckeditor
                      :config="ckeditorConfig"
                      @focus="setCurrentEditor($event)"
                      id="note"
                      v-model="releaseNoteRecord.note"
                    ></ckeditor>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-textarea
              auto-grow
              class="mt-6"
              data-cy="releaseNoteForm__input--activitiesImpacted"
              dense
              label="Other Activities Impacted"
              outlined
              rows="1"
              v-if="templateMask.fields.activitiesImpacted || releaseNoteRecord.activitiesImpacted"
              v-model="releaseNoteRecord.activitiesImpacted"
            ></v-textarea>
            <v-row dense>
              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="dropdowns.userRoles"
                  :multiple="true"
                  :rules="userRoleRules"
                  data-cy="releaseNoteForm__input--userRole"
                  dense
                  filled
                  label="User Role *"
                  v-if="templateMask.fields.userRole || releaseNoteRecord.userRole"
                  v-model="releaseNoteRecord.userRole"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="operationalizeTypeOptions"
                  :rules="operationalizeTypeComputedRules"
                  data-cy="releaseNoteForm__input--operationalizeType"
                  dense
                  filled
                  label="Operationalize Type *"
                  item-text="text"
                  item-value="value"
                  item-disabled="disabled"
                  :hint="operationalizeHelperText"
                  persistent-hint
                  v-if="templateMask.fields.operationalizeType || releaseNoteRecord.operationalizeType"
                  v-model="releaseNoteRecord.operationalizeType"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="releaseTypeOptions"
                  :rules="releaseTypeComputedRules"
                  data-cy="releaseNoteForm__input--releaseType"
                  dense
                  filled
                  label="Release Type *"
                  item-text="text"
                  item-value="value"
                  item-disabled="disabled"
                  :hint="releaseTypeHelperText"
                  persistent-hint
                  v-if="templateMask.fields.releaseType || releaseNoteRecord.releaseType"
                  v-model="releaseNoteRecord.releaseType"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  :items="dropdowns.buildType"
                  data-cy="releaseNoteForm__input--buildType"
                  dense
                  filled
                  label="Build Type"
                  v-if="templateMask.fields.buildType || releaseNoteRecord.buildType"
                  v-model="releaseNoteRecord.buildType"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col
                cols="12"
                v-if="
                  serviceAreaCategoryList &&
                    (templateMask.fields.serviceAreaAttributeList || releaseNoteRecord.serviceAreaAttributeList)
                "
              >
                <v-card class="pa-2 pt-3 mb-2">
                  <v-row justify-start>
                    <v-col cols="12" md="6" class="mr-5">
                      <v-autocomplete
                        :items="serviceAreaCategoryList"
                        dense
                        :item-text="serviceAreaCategoryDisplayText"
                        :search-input.sync="searchServiceAreaCategoryList"
                        clearable
                        item-color="primary"
                        item-value="id"
                        label="Filter Service Area Attribute by Category"
                        placeholder="Select a category to limit the attribute options displayed"
                        prepend-icon="search"
                        return-object
                        v-model="selectedServiceAreaCategory"
                      >
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="3" class="ml-4">
                      <v-checkbox
                        :false-value="false"
                        :true-value="true"
                        dense
                        label="Display deprecated attributes"
                        v-model="displayDeprecated"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  <div class="mx-4">
                    <v-autocomplete
                      :items="serviceAreaAttributeListFiltered"
                      dense
                      :item-text="serviceAreaAttributeDisplayText"
                      :search-input.sync="searchServiceAreaAttributeList"
                      deletable-chips
                      item-color="primary"
                      item-value="id"
                      label="Service Area Attributes"
                      multiple
                      outlined
                      chips
                      small-chips
                      v-model="releaseNoteRecord.serviceAreaAttributeList"
                    >
                    </v-autocomplete>
                    <span v-if="releaseNoteRecord.serviceAreaAttributeList">
                      {{ releaseNoteRecord.serviceAreaAttributeList.length }} attributes are selected.
                    </span>
                    <span v-if="selectedServiceAreaCategory"
                      >Only the <span class="font-weight-bold">{{ selectedServiceAreaCategory.name }}</span> category is
                      displayed. Clear the filter to see all selected attributes.</span
                    >
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <v-text-field
              data-cy="releaseNoteForm__input--otherUserRoles"
              dense
              label="Other User Roles"
              outlined
              v-if="templateMask.fields.otherUserRoles || releaseNoteRecord.otherUserRoles"
              v-model="releaseNoteRecord.otherUserRoles"
            ></v-text-field>
            <v-row dense class="mb-4">
              <v-col text small cols="12" md="4" v-if="templateMask.fields.qaAnalyst || releaseNoteRecord.qaAnalyst">
                <user-picker
                  attributeName="qaAnalyst"
                  :dense="true"
                  :isRequired="true"
                  :trimUserName="true"
                  filled
                  label="QA Analyst"
                  v-model="releaseNoteRecord.qaAnalyst"
                ></user-picker>
              </v-col>
              <v-col cols="12" md="4" v-if="templateMask.fields.buildAnalyst || releaseNoteRecord.buildAnalyst">
                <user-picker
                  attributeName="buildAnalyst"
                  :dense="true"
                  filled
                  label="Build Analyst"
                  v-model="releaseNoteRecord.buildAnalyst"
                ></user-picker>
              </v-col>
              <v-col
                cols="12"
                md="4"
                v-if="templateMask.fields.jiraSupportAnalyst || releaseNoteRecord.jiraSupportAnalyst"
              >
                <user-picker
                  attributeName="jiraSupportAnalyst"
                  :dense="true"
                  :trimUserName="true"
                  filled
                  :multiple="true"
                  label="JIRA Support Analyst"
                  v-model="releaseNoteRecord.jiraSupportAnalyst"
                ></user-picker>
              </v-col>
              <v-col
                cols="12"
                md="4"
                v-if="templateMask.fields.documentedOnBehalfOf || releaseNoteRecord.documentedOnBehalfOf"
              >
                <user-picker
                  attributeName="documentedOnBehalfOf"
                  :dense="true"
                  :trimUserName="true"
                  filled
                  label="Documented on Behalf of"
                  v-model="releaseNoteRecord.documentedOnBehalfOf"
                ></user-picker>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" md="3" v-if="templateMask.fields.uatJira || releaseNoteRecord.uatJira">
                <v-text-field
                  data-cy="releaseNoteForm__input--uatJira"
                  dense
                  label="UAT JIRA"
                  outlined
                  v-model="releaseNoteRecord.uatJira"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" v-if="templateMask.fields.trainingJira || releaseNoteRecord.trainingJira">
                <v-text-field
                  data-cy="releaseNoteForm__input--trainingJira"
                  dense
                  label="Training JIRA"
                  outlined
                  v-model="releaseNoteRecord.trainingJira"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" v-if="templateMask.fields.buildJira || releaseNoteRecord.buildJira">
                <v-text-field
                  data-cy="releaseNoteForm__input--buildJira"
                  dense
                  label="Build JIRA"
                  outlined
                  v-model="releaseNoteRecord.buildJira"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" v-if="templateMask.fields.qaJira || releaseNoteRecord.qaJira">
                <v-text-field
                  data-cy="releaseNoteForm__input--qaJira"
                  dense
                  label="QA JIRA"
                  outlined
                  v-model="releaseNoteRecord.qaJira"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-expansion-panels focusable class="mb-2 subtitle-1" v-show="showUatPurpose()">
              <v-expansion-panel data-cy="releaseNoteForm__input--requiredSetup">
                <v-expansion-panel-header>UAT Purpose</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <ckeditor
                      :config="ckeditorConfig"
                      @focus="setCurrentEditor($event)"
                      id="requiredSetup"
                      v-model="releaseNoteRecord.requiredSetup"
                    ></ckeditor>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-expansion-panels
              focusable
              class="subtitle-1"
              v-show="templateMask.fields.updatedReason || releaseNoteRecord.updatedReason"
            >
              <v-expansion-panel data-cy="releaseNoteForm__input--updatedReason">
                <v-expansion-panel-header>Update</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <ckeditor
                      :config="ckeditorConfig"
                      @focus="setCurrentEditor($event)"
                      id="updatedReason"
                      v-model="releaseNoteRecord.updatedReason"
                    ></ckeditor>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- eslint-disable -->
            <v-checkbox
              :false-value="0"
              :true-value="1"
              data-cy="releaseNoteForm__input--addedPostUat"
              dense
              hide-details
              label="Added Post UAT"
              v-if="templateMask.fields.addedPostUat || releaseNoteRecord.addedPostUat"
              v-model="releaseNoteRecord.addedPostUat"
            ></v-checkbox>

            <!-- eslint-enable -->
            <div class="mt-4">
              <v-btn @click="cancel" class="mr-2" outlined>Cancel</v-btn>
              <v-btn @click="save" :disabled="!formValid || isSaving" data-cy="releaseNote__submit" color="primary"
                >Save Release Note</v-btn
              >
            </div>
          </v-form>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
/* global CKEDITOR */
import Ckeditor from "vue-ckeditor2";

// es modules
import { cloneDeep } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";

import AdminDropdown from "@/components/AdministrableDropdown";
import IndividualTestSetupForm from "@/components/individualTestSetupForm";
import IndividualTestSetup from "@/components/individualTestSetupTable";
import PackInfo from "@/components/packInfo";
import FileUploader from "@/components/fileUploader";
import UserPicker from "@/components/userPicker";

// Plain-text columns that allow NULL; whitespace-only input is saved as null rather than an empty string.
const nullableTextFieldList = [
  "activitiesImpacted",
  "buildJira",
  "otherUserRoles",
  "qaJira",
  "reopenedReason",
  "trainingJira",
  "uatJira",
];

export default {
  name: "packReleaseNoteCreate",
  components: {
    AdminDropdown,
    Ckeditor,
    IndividualTestSetupForm,
    IndividualTestSetup,
    FileUploader,
    PackInfo,
    UserPicker: UserPicker,
  },
  computed: {
    needsQaAnalyst() {
      return (
        !!this.releaseNoteRecord.sourceTemplateId &&
        !this.releaseNoteRecord.qaAnalyst &&
        !!this.templateMask?.fields?.qaAnalyst
      );
    },
    sourceTemplateDisplay() {
      const { sourceTemplateId, sourceTemplateTitle } = this.releaseNoteRecord;
      return sourceTemplateTitle ? `${sourceTemplateTitle} (ID: ${sourceTemplateId})` : `ID: ${sourceTemplateId}`;
    },
    operationalizeTypeOptions() {
      return this.makeDropdownOptions({
        baseList: this.dropdowns?.operationalizeType,
        selectedValue: this.releaseNoteRecord?.operationalizeType,
        templateType: this.releaseNoteRecord?.templateType,
        allowedByTemplate: { optionalEnhancement: ["Optional"] },
      });
    },

    releaseTypeOptions() {
      return this.makeDropdownOptions({
        baseList: this.dropdowns?.releaseType,
        selectedValue: this.releaseNoteRecord?.releaseType,
        templateType: this.releaseNoteRecord?.templateType,
        allowedByTemplate: {
          optionalEnhancement: ["Optional - No JIRA Needed", "Optional - JIRA Needed"],
        },
      });
    },

    operationalizeHelperText() {
      return this.templateConstraints?.helperText?.operationalizeType || "";
    },

    releaseTypeHelperText() {
      return this.templateConstraints?.helperText?.releaseType || "";
    },

    // Validation rules that enforce allowed values when template === optionalEnhancement
    operationalizeTypeComputedRules() {
      const base = this.operationalizeTypeRules || [];
      const allowed = (this.templateConstraints?.operationalizeType || [])
        .concat(this.releaseNoteRecord?.operationalizeType || [])
        .filter(Boolean);
      if (allowed.length) {
        return [
          v => !!v || "Operationalize Type is required",
          v => (allowed.includes(v) ? true : this.templateConstraints?.validationMessage?.operationalizeType),
        ];
      }
      return base;
    },

    releaseTypeComputedRules() {
      const base = this.releaseTypeRules || [];
      const allowed = (this.templateConstraints?.releaseType || [])
        .concat(this.releaseNoteRecord?.releaseType || [])
        .filter(Boolean);
      if (allowed.length) {
        return [
          v => !!v || "Release Type is required",
          v => (allowed.includes(v) ? true : this.templateConstraints?.validationMessage?.releaseType),
        ];
      }
      return base;
    },
    serviceAreaAttributeListDeprecated() {
      const { serviceAreaAttributeList } = this;
      return serviceAreaAttributeList.filter(item => !!item.isDeprecated);
    },
    serviceAreaAttributeListFiltered() {
      let filteredList = cloneDeep(this.serviceAreaAttributeList);

      // Sort by category, then sort by name within each category
      filteredList.sort((a, b) => a.categoryId - b.categoryId || a.name.localeCompare(b.name));

      if (this.selectedServiceAreaCategory) {
        filteredList = filteredList.filter(item => {
          return item.categoryId === this.selectedServiceAreaCategory.id;
        });
      }

      if (!this.displayDeprecated) {
        // return only non-deprecated;
        return filteredList.filter(item => !item.isDeprecated);
      }

      return filteredList;
    },
    dropdowns() {
      return this.$store.state.dropdown.dropdowns;
    },
    individualSetup: {
      get() {
        return this.$store.state.releaseNote.releaseNoteRecord.individualSetup;
      },
      set(val) {
        this.$store.commit("releaseNote/setReleaseNoteIndividualSetup", val);
      },
    },
    packId() {
      return this.$route.params.packId;
    },
    releaseNoteId() {
      return this.$route.params.releaseNoteId;
    },
    releaseNoteRecord() {
      return this.$store.state.releaseNote.releaseNoteRecord;
    },
    templateTypes() {
      const templates = Object.keys(this.$store.state.templates.list);
      return templates.map(item => ({
        value: item,
        name: this.$store.state.templates.list[item].name,
      }));
    },
    sortedFields() {
      if (!this.templateMask.fields) return [];

      return Object.keys(this.templateMask.fields)
        .filter(key => this.fieldLabels[key]) // Exclude fields not in fieldLabels (legacy/deprecated fields)
        .filter(key => !this.requiredFields.includes(key)) // Exclude required fields (always visible)
        .map(key => ({
          key,
          label: this.fieldLabels[key] || key,
          value: this.templateMask.fields[key],
          isRequired: false, // All fields here are optional since required are filtered out
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    // Centralized mapping for template-specific allowed values, helper text, and validation messages.
    templateOptionMap() {
      return {
        optionalEnhancement: {
          operationalizeType: ["Optional"],
          releaseType: ["Optional - No JIRA Needed", "Optional - JIRA Needed"],
          helperText: {
            operationalizeType:
              "Only 'Optional' is valid for Optional Enhancements; other values are disabled unless already selected.",
            releaseType:
              "Select one of the Optional release types. Non-optional types are disabled unless already selected.",
          },
          validationMessage: {
            operationalizeType: "Value must be 'Optional' for optional enhancements",
            releaseType: "Select one of the allowed Optional release types",
          },
        },
      };
    },
    templateConstraints() {
      return this.templateOptionMap[(this.releaseNoteRecord?.templateType)] || null;
    },
    ...mapGetters({
      getServiceAreaAttribute: "admin/serviceAreaAttribute/getById",
      getServiceAreaCategory: "admin/serviceAreaCategory/getById",
      hasReleaseNoteCreateEdit: "session/hasReleaseNoteCreateEdit",
      fieldLabels: "templates/fieldLabels",
      requiredFields: "templates/requiredFields",
    }),
    ...mapState({
      roleName: state => state.session.user.roleName,
      userName: state => state.session.user.name,
      adminDropdownList: state => state.admin.dropdown.list,
      packList: state => state.pack.packList,
      serviceAreaAttributeList: state => state.admin.serviceAreaAttribute.list,
      serviceAreaCategoryList: state => state.admin.serviceAreaCategory.list,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.fetchAdminDropdownList(),
        this.fetchPackList(),
        this.fetchServiceAreaAttributeList(),
        this.fetchServiceAreaCategoryList(),
        this.$store.dispatch("releaseNote/resetReleaseNoteRecord"),
        this.$store.dispatch("file/resetList"),
        this.$store.dispatch("user/load"),
      ]);
    } catch (e) {
      console.error(e);
      this.$notifyError("Unable to fetch data from server");
    }

    if (this.mode === "edit") {
      await this.$store.dispatch("releaseNote/getReleaseNoteRecord", this.releaseNoteId);
      this.documentationStatusBeforeEdit = this.releaseNoteRecord.documentationStatus;
      this.updateTemplateMask(this.releaseNoteRecord.templateType);
      await this.$store.dispatch("file/list", {
        ownerId: this.releaseNoteId,
        ownerType: "releaseNote",
      });

      // If any of the Service Area Attributes are associated with this Release Note, set displayDeprecated to true.
      if (this.releaseNoteRecord.serviceAreaAttributeList) {
        const deprecatedAttributeExists = this.releaseNoteRecord.serviceAreaAttributeList.find(id => {
          const attribute = this.getServiceAreaAttribute(id);
          return attribute.isDeprecated;
        });
        if (deprecatedAttributeExists) {
          this.displayDeprecated = true;
        }
      }
    } else {
      await this.$store.dispatch("releaseNote/resetReleaseNoteRecord", this.packId);
    }

    this.initialLoadComplete = true;
  },
  data() {
    return {
      ckeditorConfig: {
        toolbarGroups: [
          { name: "basicstyles", groups: ["basicstyles", "cleanup"] },
          {
            name: "paragraph",
            groups: ["list", "indent", "blocks", "align", "bidi", "paragraph"],
          },
          { name: "links", groups: ["links"] },
          { name: "insert", groups: ["insert"] },
          "/",
          { name: "styles", groups: ["styles"] },
          { name: "colors", groups: ["colors"] },
          { name: "tools", groups: ["tools"] },
          { name: "others", groups: ["others"] },
          { name: "about", groups: ["about"] },
          {
            name: "editing",
            groups: ["find", "selection", "editing"],
          },
          { name: "forms", groups: ["forms"] },
          { name: "document", groups: ["mode", "document", "doctools"] },
          { name: "clipboard", groups: ["clipboard", "undo"] },
        ],
        removeButtons:
          "Flash,Iframe,PageBreak,Language,About,Subscript,Superscript,CopyFormatting,Anchor,Find,Replace,SelectAll,Cut,Copy,PasteFromWord,Paste,Templates,Save,NewPage,Preview,Print,Form,Checkbox,Radio,TextField,Textarea,Select,Button,ImageButton,HiddenField",
        height: 200,
        versionCheck: false,
      },
      careSettingRules: [name => !!name || "Care setting is required"],
      currentEditor: null,
      documentationStatusBeforeEdit: null,
      displayDeprecated: false,
      documentationStatusRules: [v => !!v || "Document Status is required"],
      drawer: false,
      drawerMode: null,
      formValid: false,
      isSaving: false,
      initialLoadComplete: false,
      operationalizeTypeRules: [v => !!v || "Operationalize Type is required"],
      primaryActivityRules: [v => !!v || "Primary Activity is required"],
      releaseTypeRules: [v => !!v || "Release Type is required"],
      searchServiceAreaAttributeList: null,
      searchServiceAreaCategoryList: null,
      selectedServiceAreaCategory: null,
      showTestSetupForm: false,
      templateMask: {
        name: null,
        fields: {},
      },
      templateTypeRules: [v => !!v || "Template Type is required"],
      titleRules: [v => !!v || "Title is required"],
      userRoleRules: [v => (Array.isArray(v) && v.length > 0) || "User Role is required"],
    };
  },
  methods: {
    addTestSetupItem(val) {
      this.releaseNoteRecord.individualSetup.push(val);
      this.toggleSetupForm();
    },
    normalizeTextFields() {
      nullableTextFieldList.forEach(field => {
        const value = this.releaseNoteRecord[field];
        if (typeof value !== "string") {
          return;
        }

        const trimmed = value.trim();
        this.releaseNoteRecord[field] = trimmed === "" ? null : trimmed;
      });
    },
    async save() {
      if (this.isSaving) return;
      let releaseNote;
      let {
        releaseNoteRecord: { packId },
      } = this;

      if (!packId) {
        // If packId value gets cleared from dropdown or is still 0 from the initialization in the Vuex store,
        // the value in the URL will be used by default.
        packId = this.packId;
      }

      const {
        releaseNoteRecord: { datePublished, documentationStatus },
      } = this;

      /*
      Only add the published date once.
      Check the previous value of documentation status to avoid saving a new datePublished value
      when editing of old notes that are already published.
      */
      if (
        documentationStatus === "Published" &&
        this.documentationStatusBeforeEdit !== "Published" &&
        datePublished === null
      ) {
        const currentDate = new Date();
        // set to 12am
        currentDate.setHours(0, 0, 0, 0);
        this.releaseNoteRecord.datePublished = currentDate;
      }

      // Run form validation (apply rules) before save to enforce client-side validators
      if (this.$refs.form && typeof this.$refs.form.validate === "function") {
        const ok = this.$refs.form.validate();
        if (!ok) return;
      }

      if (this.releaseNoteRecord?.templateType === "optionalEnhancement") {
        const content = this.releaseNoteRecord.optionalEnhancement;
        const textOnly = content && typeof content === "string" ? content.replace(/<[^>]*>/g, "").trim() : "";

        if (!textOnly) {
          this.$notifyError("Optional Enhancement content is required for Optional Enhancements");
          return;
        }
      }

      if (this.templateConstraints) {
        const allowedOperationalizeType = this.templateConstraints.operationalizeType || [];
        const allowedReleaseType = this.templateConstraints.releaseType || [];

        if (
          allowedOperationalizeType.length &&
          !allowedOperationalizeType.includes(this.releaseNoteRecord.operationalizeType)
        ) {
          this.$notifyError(
            this.templateConstraints.validationMessage?.operationalizeType ||
              "Invalid Operationalize Type for selected template"
          );
          return;
        }

        if (allowedReleaseType.length && !allowedReleaseType.includes(this.releaseNoteRecord.releaseType)) {
          this.$notifyError(
            this.templateConstraints.validationMessage?.releaseType || "Invalid Release Type for selected template"
          );
          return;
        }
      }

      this.normalizeTextFields();

      this.isSaving = true;
      try {
        if (this.mode === "edit") {
          releaseNote = await this.$store.dispatch("releaseNote/update", this.releaseNoteId);
        } else {
          releaseNote = await this.$store.dispatch("releaseNote/create");
        }

        this.$notifySuccess("Successfully saved release note");

        this.$router.push({
          name: "releaseNoteView",
          params: {
            packId,
            releaseNoteId: releaseNote.data.result.id,
          },
        });
      } catch (e) {
        console.error(e);
        this.$notifyError("Could not save release note");
      } finally {
        this.isSaving = false;
      }
    },
    cancel() {
      this.$router.go(-1);
      this.$store.dispatch("file/resetList");
      this.$store.dispatch("releaseNote/resetReleaseNoteRecord");
    },
    handleTemplateTypeChange() {
      this.updateTemplateMask(this.releaseNoteRecord.templateType);
      this.updateDefaultAnalyst();
    },
    insertFile(file) {
      let html;
      if (file.type === "image") {
        html = `<img style="max-width: 800px;" class="och-img__releaseNote" src="/api/file?key=${file.filename}" />`;
      } else {
        const filenameParts = file.originalFilename.split(".");
        const extension = filenameParts[filenameParts.length - 1];
        html = `<a href="/api/file?key=${file.filename}&filename=${file.originalFilename}">${
          file.originalFilename
        }</a>`;
      }
      const editor = CKEDITOR.instances[this.currentEditor];

      if (editor) {
        editor.insertHtml(html);
      } else {
        console.info("INFO: Must first click into rich-text editor window before hitting 'Insert' button for file");
        this.$notifyError("Files can only be inserted into rich-text editors");
      }
    },
    registerEventListener(editor) {
      const editableContent = editor.editable();
      editableContent.attachListener(editableContent, "blur", e => {
        if (editableContent.getData() !== this.releaseNoteRecord[editor.name]) {
          this.releaseNoteRecord[editor.name] = editableContent.getData();
        }
      });
    },
    removeTestSetupItem(idx) {
      this.individualSetup[idx].isDeleted = true;
      this.$store.commit("releaseNote/setReleaseNoteIndividualSetup", this.individualSetup);
    },
    selectDocumentedUser(val) {
      this.releaseNoteRecord.documentedOnBehalfOf = val;
    },
    selectApplication(val) {
      this.releaseNoteRecord.applicationDropdownValueId = val;
    },
    selectPrimaryActivity(val) {
      this.releaseNoteRecord.primaryActivityDropdownValueId = val;
    },
    selectQaAnalyst(val) {
      this.releaseNoteRecord.qaAnalyst = val;
    },
    selectBuildAnalyst(val) {
      this.releaseNoteRecord.buildAnalyst = val;
    },
    selectReportAnalyst(val) {
      this.releaseNoteRecord.jiraSupportAnalyst = val;
    },
    serviceAreaAttributeDisplayText(item) {
      if (item) {
        const category = this.getServiceAreaCategory(item.categoryId);
        if (category) {
          return `${category.name} - ${item.name} ${item.isDeprecated ? "(deprecated)" : ""}`;
        }
        return `${item.name}`;
      }
    },
    serviceAreaCategoryDisplayText(item) {
      if (item) {
        return `${item.name}`;
      }
    },
    setCurrentEditor({ editor }) {
      this.currentEditor = editor.name;
      this.registerEventListener(editor);
    },
    showUatPurpose() {
      return (
        this.templateMask.fields.requiredSetup ||
        this.releaseNoteRecord.requiredSetup ||
        this.releaseNoteRecord.operationalizeType === "UAT Needed"
      );
    },
    toggleSetupForm() {
      this.showTestSetupForm = !this.showTestSetupForm;
    },
    updateDefaultAnalyst() {
      if (this.mode === "create" && this.releaseNoteRecord.templateType) {
        if (this.roleName === "ochinQaAnalyst") {
          this.selectQaAnalyst(this.userName);
        }

        if (this.roleName === "ochinBiAnalyst") {
          this.selectReportAnalyst(this.userName);
        }
      }
    },
    updateDrawer(isVisible, mode = "attachments") {
      if (!isVisible && mode === this.drawerMode) {
        this.drawer = false;
        this.drawerMode = null;
      } else if (!isVisible && mode !== this.drawerMode) {
        this.drawerMode = mode;
      } else {
        this.drawer = true;
        this.drawerMode = mode;
      }
    },
    updateTemplateMask(templateType) {
      this.templateMask = this.$store.state.templates.list[templateType];
    },
    makeDropdownOptions(config = {}) {
      const { baseList, selectedValue, templateType, allowedByTemplate = {} } = config;
      const items = (baseList ?? []).map(i => ({ text: i, value: i, disabled: false }));

      const allowedForTemplate = (templateType && allowedByTemplate[templateType]) || [];

      if (templateType && allowedForTemplate.length) {
        const hasAllowed = items.some(i => allowedForTemplate.includes(i.value));
        if (hasAllowed) {
          // disable items not in allowedForTemplate
          return items.map(i => ({ ...i, disabled: !allowedForTemplate.includes(i.value) }));
        }
        // fallback: include selectedValue if missing
        const base = items;
        if (selectedValue && !base.find(b => b.value === selectedValue)) {
          return [...base, { text: selectedValue, value: selectedValue, disabled: false }];
        }
        return base;
      }

      // General case: ensure selected value remains visible when editing
      if (selectedValue && !items.find(i => i.value === selectedValue)) {
        return [...items, { text: selectedValue, value: selectedValue, disabled: false }];
      }

      return items;
    },
    ...mapActions({
      fetchAdminDropdownList: "admin/dropdown/list",
      fetchServiceAreaAttributeList: "admin/serviceAreaAttribute/list",
      fetchServiceAreaCategoryList: "admin/serviceAreaCategory/list",
      fetchPackList: "pack/getList",
    }),
  },
  props: {
    mode: {
      type: String,
      default: "create",
    },
  },
  watch: {
    "releaseNoteRecord.applicationDropdownValueId"(newVal, oldVal) {
      if (newVal && oldVal) {
        this.releaseNoteRecord.primaryActivityDropdownValueId = null;
      }
    },
  },
};
</script>

<style>
.ck-editor__editable {
  min-height: 100px;
}

.och-releaseNote__form .v-expansion-panel__header {
  margin: -5px;
}

.och-navigation-drawer {
  padding-bottom: 15px;
  padding-top: 15px;
}

.v-navigation-drawer.och-topMargin--packInfoOffset {
  padding-bottom: 200px;
  /* Vuetify override */
  position: fixed !important;
  top: 1.4rem !important;
}

.v-expansion-panel-content .v-expansion-panel-content__wrap {
  padding-top: 0;
  padding-right: 0;
  padding-bottom: 0;
  padding-left: 0;
}
</style>
